'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useRef } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { useOverlay, useOverlayItem } from '@flipster/overlay';
import { createDropdownSelect } from '@flipster/react/design-system/dropdown/dropdown-select';
import EditIcon from '@flipster/react/icon/generated/EditIcon';
import PhotoIcon from '@flipster/react/icon/generated/PhotoIcon';
import TrashIcon from '@flipster/react/icon/generated/TrashIcon';
import UserCircleIcon from '@flipster/react/icon/generated/UserCircleIcon';
import { css } from '@flipster/styled-system/css';
import { square, stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';

import { EditProfileModal } from '../..';
import { AddProfileImageModal } from '../../../add-profile-image';
import { editProfileBunja } from '../../bunja';

const ProfileImageDropdown = createDropdownSelect<'upload' | 'remove'>();

export const ProfileImage = () => {
  const overlay = useOverlay();
  const { close } = useOverlayItem();
  const { profileImagePreviewAtom, isMasterTraderAtom, draftProfileImageAtom } = useBunja(editProfileBunja);
  const isMasterTrader = useAtomValue(isMasterTraderAtom);
  const imagePreview = useAtomValue(profileImagePreviewAtom);
  const setDraftProfileImage = useSetAtom(draftProfileImageAtom);
  const inputRef = useRef<HTMLInputElement>(null);

  if (!isMasterTrader) return null;

  const hasImage = Boolean(imagePreview);

  const handleFileSelect = () => inputRef.current?.click();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const rawPreviewUrl = URL.createObjectURL(file);
    const mimeType = file.type || 'image/jpeg';
    close();
    overlay.open(({ onClose }) => (
      <AddProfileImageModal
        onClose={() => {
          URL.revokeObjectURL(rawPreviewUrl);
          onClose();
          overlay.open(() => <EditProfileModal />);
        }}
        imageSrc={rawPreviewUrl}
        mimeType={mimeType}
        onConfirm={async (croppedBlob, previewUrl) => {
          setDraftProfileImage({ type: 'change', blob: croppedBlob, previewUrl });
          URL.revokeObjectURL(rawPreviewUrl);
          onClose();
          overlay.open(() => <EditProfileModal />);
        }}
      />
    ));

    // Reset input so the same file can be re-selected
    e.target.value = '';
  };

  return (
    <div className={stack({ gap: 16, alignItems: 'center' })}>
      <input
        ref={inputRef}
        type='file'
        accept='image/*'
        onChange={handleFileChange}
        className={css({ display: 'none' })}
      />
      <div className={css({ position: 'relative', w: 'fit-content' })}>
        {/* Profile image circle */}
        {imagePreview ? (
          <img
            src={imagePreview}
            alt='Profile'
            className={css({
              w: 80,
              h: 80,
              rounded: '50%',
              objectFit: 'cover',
              bg: 'background.default.level-3',
            })}
          />
        ) : (
          <UserCircleIcon size={80} className={css({ color: 'content.default.level-5' })} />
        )}

        {/* Edit icon overlay */}
        {hasImage ? (
          <ProfileImageDropdown.Root placement='bottom-start'>
            <ProfileImageDropdown.Trigger css={{ position: 'absolute', bottom: 0, right: 0 }}>
              <div
                className={square({
                  size: 24,
                  rounded: '50%',
                  bg: 'background.accent.secondary',
                  color: 'inverse.black',
                  cursor: 'pointer',
                })}
              >
                <EditIcon size={16} />
              </div>
            </ProfileImageDropdown.Trigger>
            <ProfileImageDropdown.Select portal>
              <ProfileImageDropdown.Item
                icon={<PhotoIcon />}
                label={<FormattedMessage id='Copy.r260312.Upload_photo' />}
                value='upload'
                onClick={handleFileSelect}
              />
              <ProfileImageDropdown.Item
                icon={<TrashIcon color={token('colors.content.state.negative')} />}
                label={
                  <span className={css({ color: 'content.state.negative' })}>
                    <FormattedMessage id='Copy.r260312.Remove' />
                  </span>
                }
                value='remove'
                onClick={() => setDraftProfileImage({ type: 'remove' })}
              />
            </ProfileImageDropdown.Select>
          </ProfileImageDropdown.Root>
        ) : (
          <button
            onClick={handleFileSelect}
            className={square({
              position: 'absolute',
              bottom: 0,
              right: 0,
              size: 24,
              rounded: '50%',
              bg: 'background.accent.secondary',
              color: 'inverse.black',
              cursor: 'pointer',
            })}
          >
            <EditIcon size={16} />
          </button>
        )}
      </div>
    </div>
  );
};
